<!-- Scene Graph Panel -->
<script lang="ts">
  import {
    sceneState,
    selectObject,
    addObject,
    removeObject,
    moveObject,
  } from '../stores/sceneEditorStore';
  import { ALL_PREFABS, createObjectFromPrefab } from '../prefabs';
  import Button from '@/ui/base/Button.svelte';
  import Icon from '@/ui/base/Icon.svelte';
  import Picker from '@/ui/base/Picker.svelte';
  import { nanoid } from 'nanoid';

  let expandedNodes = new Set<string>();
  let searchQuery = '';
  let selectedTypes = new Set<string>(['sprite', 'space-bg']);

  // Picker options for adding objects
  $: prefabOptions = ALL_PREFABS.map(prefab => ({
    id: prefab.id,
    name: prefab.name,
    icon: prefab.icon,
  }));

  // Реактивная переменная для корневых объектов
  $: rootObjects = Object.values($sceneState.objects).filter(obj => !obj.parent);

  // Фильтрованные объекты с учетом поиска и типов
  $: filteredObjects = rootObjects.filter(obj => {
    const matchesSearch =
      !searchQuery ||
      obj.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      obj.type.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = selectedTypes.has(obj.type);

    return matchesSearch && matchesType;
  });

  function toggleNode(nodeId: string) {
    if (expandedNodes.has(nodeId)) {
      expandedNodes.delete(nodeId);
    } else {
      expandedNodes.add(nodeId);
    }
    expandedNodes = expandedNodes; // trigger reactivity
  }

  function handleObjectSelect(objectId: string) {
    selectObject(objectId);
  }

  function handleAddPrefab(prefabId: string) {
    const object = createObjectFromPrefab(prefabId, nanoid());
    if (object) {
      addObject(object);
    }
  }

  function handlePrefabSelect(event: CustomEvent<{ option: any }>) {
    handleAddPrefab(event.detail.option.id);
  }

  function handleDeleteObject(objectId: string, event: Event) {
    event.stopPropagation();

    const object = $sceneState.objects[objectId];
    if (object && confirm(`Are you sure you want to delete "${object.name}"?`)) {
      removeObject(objectId);
    }
  }

  function toggleTypeFilter(type: string) {
    if (selectedTypes.has(type)) {
      selectedTypes.delete(type);
    } else {
      selectedTypes.add(type);
    }
    selectedTypes = selectedTypes; // trigger reactivity
  }

  function clearSearch() {
    searchQuery = '';
  }

  function selectAllTypes() {
    selectedTypes = new Set(['sprite', 'space-bg']);
  }

  function clearAllTypes() {
    selectedTypes.clear();
  }

  // Drag & Drop functionality
  let draggedObjectId: string | null = null;
  let dropTargetId: string | null = null;
  let dropPosition: 'before' | 'after' | 'into' = 'into';

  function handleDragStart(objectId: string, event: DragEvent) {
    draggedObjectId = objectId;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', objectId);
    }
  }

  function handleDragOver(objectId: string, event: DragEvent) {
    event.preventDefault();
    if (!event.dataTransfer) return;

    event.dataTransfer.dropEffect = 'move';
    dropTargetId = objectId;

    // Определяем позицию для вставки
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const y = event.clientY - rect.top;
    const height = rect.height;

    if (y < height * 0.3) {
      dropPosition = 'before';
    } else if (y > height * 0.7) {
      dropPosition = 'after';
    } else {
      dropPosition = 'into';
    }
  }

  function handleDragLeave(event: DragEvent) {
    // Проверяем, что мы действительно вышли из целевого элемента
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    if (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    ) {
      dropTargetId = null;
    }
  }

  function handleDrop(objectId: string, event: DragEvent) {
    event.preventDefault();

    if (!draggedObjectId || draggedObjectId === objectId) {
      draggedObjectId = null;
      dropTargetId = null;
      return;
    }

    const draggedObject = $sceneState.objects[draggedObjectId];
    const targetObject = $sceneState.objects[objectId];

    if (!draggedObject || !targetObject) {
      draggedObjectId = null;
      dropTargetId = null;
      return;
    }

    // Логика перемещения объектов
    let insertBefore: string | null = null;

    if (dropPosition === 'before') {
      // Находим объект перед целевым объектом в массиве детей
      if (targetObject.parent && $sceneState.objects[targetObject.parent]) {
        const parent = $sceneState.objects[targetObject.parent];
        const targetIndex = parent.children?.indexOf(targetObject.id) ?? -1;
        if (targetIndex > 0 && parent.children) {
          insertBefore = parent.children[targetIndex - 1];
        }
      }
    } else if (dropPosition === 'after') {
      // Находим объект после целевого объекта в массиве детей
      if (targetObject.parent && $sceneState.objects[targetObject.parent]) {
        const parent = $sceneState.objects[targetObject.parent];
        const targetIndex = parent.children?.indexOf(targetObject.id) ?? -1;
        if (targetIndex >= 0 && parent.children && targetIndex < parent.children.length - 1) {
          insertBefore = parent.children[targetIndex + 1];
        }
      }
    }

    // Определяем нового родителя
    let newParentId: string | null = null;

    if (dropPosition === 'into') {
      newParentId = targetObject.id;
    } else {
      newParentId = targetObject.parent ?? null;
    }

    // Выполняем перемещение
    moveObject(draggedObject.id, newParentId, insertBefore);

    draggedObjectId = null;
    dropTargetId = null;
  }

  function handleDragEnd() {
    draggedObjectId = null;
    dropTargetId = null;
  }

  function renderObjectNode(object: any, depth = 0) {
    const isExpanded = expandedNodes.has(object.id);
    const hasChildren = object.children && object.children.length > 0;
    const isSelected = $sceneState.selectedObjectId === object.id;

    return {
      object,
      depth,
      isExpanded,
      hasChildren,
      isSelected,
    };
  }
</script>

<div class="scene-graph h-full flex flex-col">
  <!-- Header - Unity style compact -->
  <div class="p-2 border-b border-gray-700 bg-gray-800/50">
    <div class="flex items-center justify-between mb-2">
      <h3 class="text-sm font-medium text-white">Hierarchy</h3>
      <div class="flex gap-1">
        <Button variant="secondary" size="xs" on:click={selectAllTypes} title="Show all">
          <Icon name="eye" size="xs" />
        </Button>
        <Button variant="secondary" size="xs" on:click={clearAllTypes} title="Hide all">
          <Icon name="eye-off" size="xs" />
        </Button>
      </div>
    </div>

    <!-- Search - compact -->
    <div class="relative mb-2">
      <Icon
        name="search"
        size="xs"
        class="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
      />
      <input
        type="text"
        placeholder="Search..."
        class="w-full pl-6 pr-6 py-0.5 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:border-neon-purple"
        bind:value={searchQuery}
      />
      {#if searchQuery}
        <button
          type="button"
          class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
          on:click={clearSearch}
          title="Clear search"
        >
          <Icon name="x" size="xs" />
        </button>
      {/if}
    </div>

    <!-- Add Object Picker -->
    <div class="mb-2">
      <Picker
        options={prefabOptions}
        placeholder="Add Object"
        size="xs"
        on:select={handlePrefabSelect}
        className="w-full"
      />
    </div>

    <!-- Type Filters - compact -->
    <div class="flex gap-1">
      <Button
        variant={selectedTypes.has('sprite') ? 'primary' : 'secondary'}
        size="xs"
        on:click={() => toggleTypeFilter('sprite')}
        title="Sprites"
      >
        <Icon name="sprite" size="xs" />
      </Button>
      <Button
        variant={selectedTypes.has('space-bg') ? 'primary' : 'secondary'}
        size="xs"
        on:click={() => toggleTypeFilter('space-bg')}
        title="Backgrounds"
      >
        <Icon name="space-bg" size="xs" />
      </Button>
    </div>
  </div>

  <!-- Object Tree - Unity style compact -->
  <div class="flex-1 overflow-y-auto" on:wheel|stopPropagation>
    {#each filteredObjects as object (object.id)}
      {@const node = renderObjectNode(object)}
      <div class="object-node group">
        <div class="w-full relative items-center" style="padding-left: {node.depth * 16}px">
          <!-- Expand/Collapse button -->
          {#if node.hasChildren}
            <button
              type="button"
              class="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white transition-colors mr-1"
              on:click={() => toggleNode(node.object.id)}
              aria-expanded={node.isExpanded}
              aria-label={node.isExpanded ? 'Collapse' : 'Expand'}
              title={node.isExpanded ? 'Collapse' : 'Expand'}
            >
              <Icon name={node.isExpanded ? 'chevron-down' : 'chevron-right'} size="xs" />
            </button>
          {:else}
            <div class="w-4 mr-1"></div>
          {/if}

          <!-- Object row -->
          <div
            class="flex-1 flex items-center gap-2 px-2 py-1 rounded text-sm transition-all duration-150
                       {node.isSelected
              ? 'bg-neon-purple/20 text-neon-purple'
              : dropTargetId === node.object.id
                ? 'bg-blue-500/20 border border-blue-400 border-dashed'
                : 'hover:bg-gray-700/30'}"
            draggable="true"
            role="button"
            tabindex="0"
            on:dragstart={e => handleDragStart(node.object.id, e)}
            on:dragover={e => handleDragOver(node.object.id, e)}
            on:dragleave={handleDragLeave}
            on:drop={e => handleDrop(node.object.id, e)}
            on:dragend={handleDragEnd}
          >
            <button
              type="button"
              class="flex-1 flex items-center gap-1 text-left min-w-0"
              on:click={() => handleObjectSelect(node.object.id)}
              title={`Select ${node.object.name} (${node.object.type})`}
            >
              <Icon name={node.object.type === 'sprite' ? 'sprite' : 'space-bg'} size="sm" />
              <span class="flex-1 truncate font-medium">{node.object.name}</span>
            </button>

            <!-- Delete button - Unity style -->
            <button
              type="button"
              class="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
              on:click={e => handleDeleteObject(node.object.id, e)}
              title={`Delete ${node.object.name}`}
            >
              <Icon name="x" size="sm" />
            </button>
          </div>
        </div>

        <!-- Children - recursive rendering -->
        {#if node.hasChildren && node.isExpanded}
          {#each node.object.children as childId}
            {@const child = $sceneState.objects[childId]}
            {#if child}
              {@const childNode = renderObjectNode(child, node.depth + 1)}
              <div class="object-node group">
                <div
                  class="w-full relative flex items-center"
                  style="padding-left: {childNode.depth * 16}px"
                >
                  <!-- Expand/Collapse button -->
                  {#if childNode.hasChildren}
                    <button
                      type="button"
                      class="w-4 h-4 flex items-center justify-center text-gray-400 hover:text-white transition-colors mr-1"
                      on:click={() => toggleNode(childNode.object.id)}
                      aria-expanded={childNode.isExpanded}
                      aria-label={childNode.isExpanded ? 'Collapse' : 'Expand'}
                      title={childNode.isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <Icon
                        name={childNode.isExpanded ? 'chevron-down' : 'chevron-right'}
                        size="xs"
                      />
                    </button>
                  {:else}
                    <div class="w-4 mr-1"></div>
                  {/if}

                  <!-- Object row -->
                  <div
                    class="flex-1 flex items-center gap-2 px-2 py-1 rounded text-sm transition-all duration-150
                               {childNode.isSelected
                      ? 'bg-neon-purple/20 text-neon-purple'
                      : dropTargetId === childNode.object.id
                        ? 'bg-blue-500/20 border border-blue-400 border-dashed'
                        : 'hover:bg-gray-700/30'}"
                    draggable="true"
                    role="button"
                    tabindex="0"
                    on:dragstart={e => handleDragStart(childNode.object.id, e)}
                    on:dragover={e => handleDragOver(childNode.object.id, e)}
                    on:dragleave={handleDragLeave}
                    on:drop={e => handleDrop(childNode.object.id, e)}
                    on:dragend={handleDragEnd}
                  >
                    <button
                      type="button"
                      class="flex-1 flex items-center gap-1 text-left min-w-0"
                      on:click={() => handleObjectSelect(childNode.object.id)}
                      title={`Select ${childNode.object.name} (${childNode.object.type})`}
                    >
                      <Icon
                        name={childNode.object.type === 'sprite' ? 'sprite' : 'space-bg'}
                        size="sm"
                      />
                      <span class="flex-1 truncate font-medium">{childNode.object.name}</span>
                    </button>

                    <!-- Delete button -->
                    <button
                      type="button"
                      class="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      on:click={e => handleDeleteObject(childNode.object.id, e)}
                      title={`Delete ${childNode.object.name}`}
                    >
                      <Icon name="x" size="sm" />
                    </button>
                  </div>
                </div>
              </div>
            {/if}
          {/each}
        {/if}
      </div>
    {/each}

    {#if filteredObjects.length === 0}
      <div class="text-center text-gray-500 py-2 px-2">
        <Icon name="empty" size="sm" class="mx-auto mb-1" />
        <p class="text-xs">
          {#if rootObjects.length === 0}
            No objects in scene
          {:else}
            No objects match your filters
          {/if}
        </p>
        <p class="text-xs text-gray-600">
          {#if rootObjects.length === 0}
            Add objects using picker above
          {:else}
            Try adjusting your search or filters
          {/if}
        </p>
      </div>
    {/if}
  </div>
</div>
